import { createFileRoute, Link } from '@tanstack/react-router'
import { SignInButton, SignUpButton, SignedIn, SignedOut } from '@clerk/clerk-react'
import { useState } from 'react'
import { FlashcardCard } from '@/components/FlashcardCard'
import { WaveformAudioPlayer } from '@/components/WaveformAudioPlayer'
import { DemoHistoryDrawer } from '@/components/DemoHistoryDrawer'

export const Route = createFileRoute('/')({
  component: SplashPage,
})

// Demo flashcard data (hardcoded mover card)
const DEMO_CARD = {
  frontSpanish: ['Para mover, mueva montañas con pasión'],
  backEnglish: ['To move, move mountains with passion'],
  targetVerb: 'mover',
  sourceVerb: 'move',
  audioUrl: '/audio/mover-spanish.mp3', // Spanish-only version (742KB)
  audioId: 'demo-mover-card',
}

// Grade button configuration (Anki 0-5 scale) - Matching app/flashcards styling
const GRADE_BUTTONS = [
  { grade: 0 as const, label: '0', desc: 'Forgot', color: 'bg-red-600' },
  { grade: 1 as const, label: '1', desc: 'Hard', color: 'bg-orange-400' },
  { grade: 2 as const, label: '2', desc: 'OK', color: 'bg-yellow-600' },
  { grade: 3 as const, label: '3', desc: 'Good', color: 'bg-blue-600' },
  { grade: 4 as const, label: '4', desc: 'Easy', color: 'bg-teal-600' },
  { grade: 5 as const, label: '5', desc: 'Perfect', color: 'bg-green-600' },
] as const

// Demo review type
interface DemoReview {
  id: string
  timestamp: number
  grade: 0 | 1 | 2 | 3 | 4 | 5
  card: {
    frontSpanish: string
    backEnglish: string
  }
}

function SplashPage() {
  // Demo state management (session-only, no persistence)
  const [reviews, setReviews] = useState<DemoReview[]>([])
  const [isFlipped, setIsFlipped] = useState(false)
  const [showHistory, setShowHistory] = useState(false)

  // Handle grade button click
  const handleGrade = (grade: 0 | 1 | 2 | 3 | 4 | 5) => {
    const newReview: DemoReview = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      grade,
      card: {
        frontSpanish: DEMO_CARD.frontSpanish[0],
        backEnglish: DEMO_CARD.backEnglish[0],
      },
    }

    setReviews(prev => [newReview, ...prev]) // Newest first
    setIsFlipped(false) // Reset card for next review
  }

  return (
    <div className="min-h-screen bg-black text-white font-mono flex flex-col items-center justify-center p-4 sm:p-6 md:p-8">
      {/* Main Container */}
      <div className="border-4 border-sky-blue max-w-5xl w-full bg-black shadow-[0_0_30px_rgba(14,165,233,0.4)] relative">

        {/* Header with Logo and Sign In */}
        <div className="border-b-4 border-sky-blue p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Jinglearn Logo - Daffiys Font */}
          <div className="flex-1 w-full flex justify-center sm:justify-start">
            <h1
              className="text-sky-blue text-5xl sm:text-6xl lg:text-[76px] leading-none tracking-wide"
              style={{ fontFamily: 'Daffiys, Courier New, monospace' }}
            >
              Jinglearn
            </h1>
          </div>

          {/* Sign In & Sign Up Buttons - Top Right */}
          <SignedOut>
            <div className="flex gap-2 sm:gap-3">
              <SignInButton mode="modal">
                <button className="px-4 sm:px-6 py-2 border-2 border-sky-blue bg-black hover:bg-sky-blue/10 text-white font-bold text-sm sm:text-base transition-all hover:shadow-[0_0_15px_rgba(14,165,233,0.6)] whitespace-nowrap">
                  [SIGN IN]
                </button>
              </SignInButton>
              <SignUpButton mode="modal">
                <button className="px-4 sm:px-6 py-2 border-2 border-sky-blue bg-black hover:bg-sky-blue/10 text-white font-bold text-sm sm:text-base transition-all hover:shadow-[0_0_15px_rgba(14,165,233,0.6)] whitespace-nowrap">
                  [SIGN UP]
                </button>
              </SignUpButton>
            </div>
          </SignedOut>

          <SignedIn>
            <Link
              to="/app/schedule"
              className="px-4 sm:px-6 py-2 border-2 border-terminal-green bg-black hover:bg-terminal-green/10 text-terminal-green font-bold text-sm sm:text-base text-center transition-all hover:shadow-[0_0_15px_rgba(16,185,129,0.6)] whitespace-nowrap"
            >
              [ENTER APP]
            </Link>
          </SignedIn>
        </div>

        {/* Main Content Area */}
        <div className="px-6 pt-1.5 pb-6 sm:px-8 sm:pt-2 sm:pb-8 md:px-12 md:pt-2.5 md:pb-12 space-y-8">

          {/* Clickable Tagline with Musical Notes */}
          <SignedOut>
            <SignUpButton mode="modal">
              <button className="w-full text-center group">
                <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white hover:text-sky-blue transition-colors duration-300 animate-pulse">
                  🎵 Learn Languages Through Music 🎶
                </p>
              </button>
            </SignUpButton>
          </SignedOut>

          <SignedIn>
            <div className="text-center">
              <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white animate-pulse">
                🎵 Learn Languages Through Music 🎶
              </p>
            </div>
          </SignedIn>

          {/* Subtitle */}
          <div className="text-center">
            <p className="text-sm sm:text-base md:text-lg text-trail-tan">
              Listen to and create catchy songs in any language • Extract vocabulary • Master with spaced repetition
            </p>
          </div>

          {/* Interactive Demo Section */}
          <div className="border-4 border-terminal-green px-6 pt-1.5 pb-6 sm:px-8 sm:pt-2 sm:pb-8 md:px-12 md:pt-2.5 md:pb-12 space-y-6 bg-black/50">
            <div className="flex items-center gap-2 mb-6 justify-center">
              <span className="text-terminal-green text-xl sm:text-2xl">♪</span>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-terminal-green text-center tracking-wider">
                Try Jinglearn Now
              </h2>
              <span className="text-terminal-green text-xl sm:text-2xl">♫</span>
            </div>

            {/* Audio Player - Above Card */}
            <div className="max-w-2xl mx-auto">
              <WaveformAudioPlayer
                audioUrl={DEMO_CARD.audioUrl}
                audioId={DEMO_CARD.audioId}
                title="mover - Spanish Verb Song"
              />
            </div>

            {/* Flashcard */}
            <div className="max-w-2xl mx-auto">
              <FlashcardCard
                frontSpanish={DEMO_CARD.frontSpanish}
                backEnglish={DEMO_CARD.backEnglish}
                targetVerb={DEMO_CARD.targetVerb}
                sourceVerb={DEMO_CARD.sourceVerb}
                isFlipped={isFlipped}
                onFlip={() => setIsFlipped(!isFlipped)}
              />
            </div>

            {/* Grade Buttons - Matching app/flashcards styling */}
            <div className="max-w-4xl mx-auto px-2">
              <p className="text-trail-tan text-sm sm:text-base text-center mb-3 font-mono">
                How well did you know this?
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-6">
                {GRADE_BUTTONS.map((btn) => (
                  <button
                    key={btn.grade}
                    onClick={() => handleGrade(btn.grade)}
                    disabled={!isFlipped}
                    className={`${btn.color} text-white font-bold font-mono text-lg sm:text-xl py-3.5 sm:py-5 px-2 sm:px-4 border-4 border-dotted border-white rounded-lg
                      transition-all min-h-[50px] sm:min-h-[65px] w-full
                      ${isFlipped ? 'hover:border-terminal-green cursor-pointer opacity-100' : 'opacity-40 cursor-not-allowed'}`}
                    type="button"
                  >
                    <div className="text-2xl sm:text-3xl mb-1">{btn.label}</div>
                    <div className="text-xs sm:text-sm">{btn.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* View History Button - Only show after first review */}
            {reviews.length > 0 && (
              <div className="text-center">
                <button
                  onClick={() => setShowHistory(true)}
                  className="px-6 py-3 border-2 border-sky-blue bg-black
                    hover:bg-sky-blue/10 text-sky-blue font-bold transition-all"
                  type="button"
                >
                  View History ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
                </button>
              </div>
            )}
          </div>

          {/* Features Section */}
          <div className="border-4 border-sky-blue px-4 pt-1 pb-4 sm:px-6 sm:pt-1.5 sm:pb-6 md:px-8 md:pt-2 md:pb-8 bg-black/50">
            <div className="flex items-center gap-2 mb-2 justify-center">
              <span className="text-sky-blue text-xl sm:text-2xl">♪</span>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-sky-blue text-center tracking-wider">
                [FEATURES]
              </h2>
              <span className="text-sky-blue text-xl sm:text-2xl">♫</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3">
                  <span className="text-sky-blue text-xl sm:text-2xl flex-shrink-0">♪</span>
                  <p className="text-sm sm:text-base text-white">
                    <span className="text-sky-blue font-bold">Listen to and create catchy songs</span> in any language with AI
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-terminal-green text-xl sm:text-2xl flex-shrink-0">►</span>
                  <p className="text-sm sm:text-base text-white">
                    <span className="text-terminal-green font-bold">Extract vocabulary</span> from lyrics automatically
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-sky-blue text-xl sm:text-2xl flex-shrink-0">♫</span>
                  <p className="text-sm sm:text-base text-white">
                    <span className="text-sky-blue font-bold">Study with spaced repetition</span> using SuperMemo & Memrise algorithms
                  </p>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3">
                  <span className="text-terminal-green text-xl sm:text-2xl flex-shrink-0">♪</span>
                  <p className="text-sm sm:text-base text-white">
                    <span className="text-terminal-green font-bold">Track your progress</span> on the calendar
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-sky-blue text-xl sm:text-2xl flex-shrink-0">►</span>
                  <p className="text-sm sm:text-base text-white">
                    <span className="text-sky-blue font-bold">View analytics</span> and level up your skills
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-terminal-green text-xl sm:text-2xl flex-shrink-0">♫</span>
                  <p className="text-sm sm:text-base text-white">
                    <span className="text-terminal-green font-bold">Custom decks</span> for personalized learning
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <SignedOut>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <SignUpButton mode="modal">
                <button className="px-6 sm:px-8 py-3 sm:py-4 border-4 border-terminal-green bg-black hover:bg-terminal-green/10 text-terminal-green font-bold text-base sm:text-lg transition-all hover:shadow-[0_0_20px_rgba(16,185,129,0.8)] hover:scale-105 w-full sm:w-auto">
                  ♪ SIGN UP FREE ♫
                </button>
              </SignUpButton>

              <Link
                to="/preview/schedule"
                className="px-6 sm:px-8 py-3 sm:py-4 border-4 border-sky-blue bg-black hover:bg-sky-blue/10 text-sky-blue font-bold text-base sm:text-lg text-center transition-all hover:shadow-[0_0_20px_rgba(14,165,233,0.8)] hover:scale-105 w-full sm:w-auto flex flex-col items-center gap-1"
              >
                <span>♫ SEE PLATFORM PREVIEW ♪</span>
                <span className="text-xs sm:text-sm font-normal opacity-80">(no signup required)</span>
              </Link>
            </div>
          </SignedOut>

          <SignedIn>
            <div className="flex justify-center pt-4">
              <Link
                to="/app/schedule"
                className="px-6 sm:px-8 py-3 sm:py-4 border-4 border-terminal-green bg-black hover:bg-terminal-green/10 text-terminal-green font-bold text-base sm:text-lg text-center transition-all hover:shadow-[0_0_20px_rgba(16,185,129,0.8)] hover:scale-105"
              >
                ♪ ENTER JINGLEARN ♫
              </Link>
            </div>
          </SignedIn>
        </div>
      </div>

      {/* History Drawer */}
      <DemoHistoryDrawer
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        reviews={reviews}
      />

      {/* Ambient glow effect */}
      <div className="fixed inset-0 pointer-events-none bg-gradient-to-b from-transparent via-sky-blue/5 to-transparent bg-[length:100%_4px] animate-[pulse_4s_ease-in-out_infinite]" />
    </div>
  )
}
